'use client';

import type { Form } from '../../../payload-types';
import type { SubjectAttributes } from '../../../utilities/change-requests/subject-marks';
import FormView from '../form-view';
import useSubmitFormSubmission from '../../../utilities/hooks/use-submit-form-submission';
import { isBookingMode, resolveBookingPage } from '../../../utilities/forms/booking-page';
import { BookingButton } from '../booking-button';
import { FormPrivacyPolicyLink } from '../form-privacy-policy-link';

export const InlineHeroForm: React.FC<{
  form: Form;
  heading?: string | null;
  subtitle?: string | null;
  ctaText?: string | null;
  successText?: string | null;
  privacyText?: string | null;
  fixedFields?: Record<string, string>;
  redirectTo?: string;
  /** The Subject attribute naming the form, from the server caller (see `subjectAttributes`). */
  subject?: SubjectAttributes;
}> = ({
  form,
  heading,
  subtitle,
  ctaText,
  successText,
  privacyText,
  fixedFields,
  redirectTo,
  subject,
}) => {
  const [result, action, isPending] = useSubmitFormSubmission({ form, redirectOverride: redirectTo });

  // The hero slots refuse booking Forms, but one stored before they did shows
  // a button that opens its Booking Page, never its (empty) fields; with no
  // Booking Page it shows nothing. Success text, fixed fields and the
  // redirect need a submission, so they go unused.
  const bookingPage = resolveBookingPage(form);
  if (isBookingMode(form.integrationMode) && !bookingPage) { return null; }

  return (
    <div {...subject} className="card bg-base-100 shadow-box">
      <div className="card-body">
        {heading ? (
          <h2 className="card-title text-base">{heading}</h2>
        ) : null}

        {subtitle ? (
          <p className="text-sm opacity-70">{subtitle}</p>
        ) : null}

        {bookingPage ? (
          <>
            <BookingButton
              form={form}
              bookingPage={bookingPage}
              text={ctaText}
              className="btn btn-primary btn-block mt-4"
            />
            {/* Where the field form linked its privacy policy, the booking button does too. */}
            <FormPrivacyPolicyLink policy={form.privacyPolicy} className="mt-3 text-center" />
          </>
        ) : result?.status === 'success' ? (
          <div className="alert alert-success mt-2">
            {successText ?? 'Submitted successfully.'}
          </div>
        ) : (
          <FormView
            form={form}
            isPending={isPending}
            action={action}
            fixedFields={fixedFields}
            submitText={ctaText ?? 'Submit'}
            submitClassName="btn-block"
            actionClassName="mt-4"
            privacyPolicyClassName="text-center"
          />
        )}

        {privacyText ? (
          <p className="text-xs text-center opacity-60 mt-2">{privacyText}</p>
        ) : null}
      </div>
    </div>
  );
};
